import React, { useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Navigation } from "../Landing/components/Navigation";
import { Footer } from "../Landing/components/Footer";
import type { Page } from "../types/Page";
import { motion } from "framer-motion";
import { useSecureVideoStream } from "../../hooks/useSecureVideoStream";
import "./ContactPage.css";

const HLS_STREAM_URL =
  "https://stream.mux.com/tLkHO1qZoaaQOUeVWo8hEBeGQfySP02EPS02BmnNFyXys.m3u8";

export const ContactPage: Page = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const contactHeroCardRef = useRef<HTMLDivElement>(null);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });

  const [isSending, setIsSending] = useState(false);
  const [isSent, setIsSent] = useState(false);

  // Secure HLS Stream with Viewport Intersection Auto-Pause
  const { videoProps } = useSecureVideoStream(videoRef, {
    src: HLS_STREAM_URL,
    isHls: true,
    containerRef: contactHeroCardRef,
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSending(true);

    try {
      const response = await fetch("/api/contact/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to send message");
      }

      setIsSending(false);
      setIsSent(true);

      setFormData({
        name: "",
        email: "",
        company: "",
        subject: "",
        message: "",
      });

      setTimeout(() => setIsSent(false), 5000);
    } catch (error) {
      console.error("Error submitting contact form:", error);
      setIsSending(false);

      const subject = encodeURIComponent(
        `[${formData.subject || "Inquiry"}] from ${formData.name}`
      );
      const body = encodeURIComponent(
        `Name: ${formData.name}\nEmail: ${formData.email}\nCompany: ${formData.company}\n\nMessage:\n${formData.message}`
      );
      window.location.href = `mailto:support@cynaps.xyz?subject=${subject}&body=${body}`;
      setIsSent(true);
      setTimeout(() => setIsSent(false), 5000);
    }
  };

  const scrollToForm = () => {
    const el = document.getElementById("contact-form-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="contact-page-root">
      {/* 1. SOLID BLACK NAVBAR */}
      <Navigation />

      {/* 2. CURVED HERO CARD: HEADING & ANIMATION HERE */}
      <section className="contact-hero-wrapper">
        <div
          ref={contactHeroCardRef}
          className="contact-hero-card"
          onContextMenu={(e) => e.preventDefault()}
        >
          {/* Right Animation Plate inside Card */}
          <div className="contact-hero-animation-box">
            <video
              ref={videoRef}
              className="contact-hero-video"
              {...videoProps}
            />

            {/* Smooth transition scrim gradient from left to right */}
            <div className="contact-hero-scrim" />
            <div className="contact-hero-bottom-scrim" />

            {/* Three thin vertical grid lines across card */}
            <div className="contact-hero-grid-line left-[25%]" />
            <div className="contact-hero-grid-line left-[50%]" />
            <div className="contact-hero-grid-line left-[75%]" />

            {/* Top Central Horizontal SVG Ellipse Glow */}
            <div className="contact-hero-center-glow">
              <svg
                viewBox="0 0 1000 240"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full"
              >
                <defs>
                  <filter
                    id="cynaps-glow-filter"
                    x="-50%"
                    y="-50%"
                    width="200%"
                    height="200%"
                    filterUnits="userSpaceOnUse"
                  >
                    <feGaussianBlur stdDeviation="25" />
                  </filter>
                  <radialGradient
                    id="cynaps-radial"
                    cx="50%"
                    cy="10%"
                    r="60%"
                    fx="50%"
                    fy="10%"
                  >
                    <stop offset="0%" stopColor="#1cd9a2" stopOpacity="0.28" />
                    <stop offset="50%" stopColor="#0b5e47" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#060707" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <ellipse
                  cx="500"
                  cy="60"
                  rx="380"
                  ry="85"
                  fill="url(#cynaps-radial)"
                  filter="url(#cynaps-glow-filter)"
                />
              </svg>
            </div>
          </div>

          {/* Simple Clean Floating Glass Card on Right */}
          <div className="contact-hero-glass-anchor hidden lg:block">
            <div className="liquid-glass-card">
              <div className="card-tag">[ Contact ]</div>
              <h3 className="card-headline">
                Talk to our <span className="italic-industry">Team</span>
              </h3>
              <div className="card-desc">
                Direct consultation for data labeling, platform access, and custom evaluation.
              </div>
            </div>
          </div>

          {/* Left Text Content: Clean Heading and Real Information */}
          <div className="contact-hero-content">
            <h1 className="contact-hero-title">
              ACCELERATE YOUR
              <br />
              MEDICAL AI.
            </h1>

            <p className="contact-hero-desc">
              Ready to scale your clinical data labeling with board-certified specialists?
              Send us a message and our team will get back to you.
            </p>

            <div className="contact-hero-actions">
              <button
                type="button"
                onClick={scrollToForm}
                className="contact-hero-btn"
              >
                <span>Send a Message ↓</span>
                <ArrowRight className="w-3.5 h-3.5 stroke-[2.5]" />
              </button>

              <a
                href="mailto:support@cynaps.xyz"
                className="contact-hero-btn-secondary"
              >
                support@cynaps.xyz
              </a>
            </div>

            <div className="contact-hero-stats">
              <div>
                <strong>&lt; 24h</strong> Response Time
              </div>
              <div className="contact-hero-stats-dot" />
              <div>
                <strong>Enterprise</strong> Support
              </div>
              <div className="contact-hero-stats-dot" />
              <div>
                <strong>HIPAA &amp; SOC 2</strong>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. MAIN CONTENT: SIMPLE CONTACT SECTION */}
      <section className="contact-section" id="contact-form-section">
        {/* Info Cards */}
        <div className="contact-info-grid">
          <div className="contact-info-card">
            <div className="contact-info-label">Email Support</div>
            <a href="mailto:support@cynaps.xyz" className="contact-info-value">
              support@cynaps.xyz
            </a>
            <div className="contact-info-desc">
              Direct inquiries and general support
            </div>
          </div>

          <div className="contact-info-card">
            <div className="contact-info-label">Response Time</div>
            <div className="contact-info-value">Within 24 Hours</div>
            <div className="contact-info-desc">
              Monday through Friday
            </div>
          </div>

          <div className="contact-info-card">
            <div className="contact-info-label">Enterprise</div>
            <div className="contact-info-value">Custom Solutions</div>
            <div className="contact-info-desc">
              Dedicated SLAs and compliance requirements
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div className="contact-form-wrap">
          <div className="contact-form-header">
            <h2 className="contact-form-heading">Send a Message</h2>
            <p className="contact-form-sub">
              Fill out the form below and our team will get back to you within 24 hours.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="contact-form-grid">
              <div className="contact-field-group">
                <label htmlFor="name" className="contact-label">
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSent}
                  className="contact-input"
                  placeholder="John Doe"
                />
              </div>

              <div className="contact-field-group">
                <label htmlFor="email" className="contact-label">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSent}
                  className="contact-input"
                  placeholder="john@company.com"
                />
              </div>
            </div>

            <div className="contact-field-group">
              <label htmlFor="company" className="contact-label">
                Company / Organization
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                disabled={isSent}
                className="contact-input"
                placeholder="Company or Institution name"
              />
            </div>

            <div className="contact-field-group">
              <label htmlFor="subject" className="contact-label">
                Subject
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                disabled={isSent}
                className="contact-select"
              >
                <option value="">Select a topic...</option>
                <option value="sales">Sales &amp; Pricing Inquiry</option>
                <option value="annotation">Data Labeling &amp; Annotation</option>
                <option value="support">Technical Support</option>
                <option value="partnership">Partnership</option>
                <option value="other">General Inquiry</option>
              </select>
            </div>

            <div className="contact-field-group">
              <label htmlFor="message" className="contact-label">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                disabled={isSent}
                className="contact-textarea"
                placeholder="How can we help you?"
              />
            </div>

            {/* Simple Sharp-Edged Submit Button with bg: #E8E4D9 */}
            <motion.button
              type="submit"
              disabled={isSending || isSent}
              whileTap={!isSending && !isSent ? { scale: 0.99 } : {}}
              className="contact-submit-btn"
            >
              {isSending ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-black/40 border-t-black rounded-full animate-spin" />
                  Sending Message...
                </span>
              ) : isSent ? (
                <span>✓ Message Sent Successfully</span>
              ) : (
                <>
                  <span>Send Message</span>
                  <ArrowRight className="w-4 h-4 stroke-[2.5]" />
                </>
              )}
            </motion.button>
          </form>
        </div>
      </section>

      {/* 4. Simple Response Time Section */}
      <section className="contact-response-section">
        <div className="contact-response-tag">// Response Time</div>
        <p className="contact-response-text">
          We typically respond within{" "}
          <span className="contact-response-highlight">24 hours</span> on business days.
        </p>
      </section>

      {/* 5. Original Cynaps Footer */}
      <Footer />
    </div>
  );
};

ContactPage.title = "Contact Us";
ContactPage.path = "/contact";
ContactPage.exact = true;

export default ContactPage;
